import { createSlice } from '@reduxjs/toolkit';
import {
    getCourses,
    getCourse,
    deleteCourse as deleteCourseApi,
    getUnits,
    createCourse as createCourseApi,
    editCourse as editCourseApi,
    copyCourse as copyCourseApi
} from 'api/courses';
import { download } from 'utils';
import { json2csv } from 'json-2-csv';
import { QUALIFICATION_LEVEL_NAMES } from 'constants';
import { generateDocumentDefinition } from 'utils/pdf/generateCourseSummary';
import { formatDate, generatePdf } from 'utils/pdf/common';

const initialState = {
    total: {
        all: 0,
        items: 0,
        private: 0,
        public: 0
    },
    pagination: {
        pageNumber: 0,
        pageSize: 10
    },
    list: [],
    units: [],
    loading: false,
    loadingUnits: false
};

const courses = createSlice({
    name: 'courses',
    initialState,
    reducers: {
        setLoading(state, action) {
            state.loading = action.payload;
        },
        setLoadingUnits(state, action) {
            state.loadingUnits = action.payload;
        },
        setCourses(state, action) {
            state.total.all = action.payload.pagination?.total || 0;
            state.total.items = action.payload.pagination?.totalItems || 0;
            state.total.private = action.payload?.meta?.totalPrivate || 0;
            state.total.public = action.payload?.meta?.totalPublic || 0;
            state.list = action.payload?.data || [];
        },
        setPagination(state, action) {
            state.pagination = action.payload;
        },
        setUnits(state, action) {
            state.units = action.payload;
        }
    }
});

export default courses.reducer;

export function fetchCourses(params) {
    return async (dispatch, getState) => {
        try {
            dispatch(courses.actions.setLoading(true));
            const { pagination } = getState().courses;

            // Merge provided params with current pagination
            const query = {
                pagination: params?.pagination || pagination,
                search: params?.search || ''
            };

            const result = await getCourses({ ...query.pagination, search: query.search });

            dispatch(courses.actions.setCourses(result || []));

            if (params?.pagination) {
                dispatch(courses.actions.setPagination(params.pagination));
            }
        } catch (error) {
            dispatch(courses.actions.setCourses([]));
        } finally {
            dispatch(courses.actions.setLoading(false));
        }
    };
}

export function deleteCourse(ids) {
    return async (dispatch) => {
        try {
            dispatch(courses.actions.setLoading(true));
            await deleteCourseApi(ids);
            await dispatch(fetchCourses());
        } catch (error) {
            dispatch(courses.actions.setCourses([]));
        } finally {
            dispatch(courses.actions.setLoading(false));
        }
    };
}

export function exportCourses(id) {
    return async () => {
        try {
            const course = await getCourse(id);

            let { title, level, units } = course?.data || {};

            const formattedUnits = units.map(({ unitcode, title, profiles }) => {
                let {
                    profile: { learning, reading, writing, oralcommunication, numeracy }
                } = profiles.find((profile) => profile.level === level);

                return {
                    'Unit Code': unitcode,
                    'Unit Title': title,
                    Learning: learning,
                    Reading: reading,
                    Writing: writing,
                    'Oral Communication': oralcommunication,
                    Numeracy: numeracy
                };
            });

            let csv = json2csv(formattedUnits);

            download(`${title} - ${QUALIFICATION_LEVEL_NAMES[level]}.csv`, 'text/csv;charset=utf-8', csv);
        } catch (error) {
            console.error(error);
        }
    };
}

export function printCourse(courseId, searchText = null) {
    return async () => {
        try {
            const getCourseResult = await getCourse(courseId);
            const course = getCourseResult?.data || {};
            const unitcodes = course?.units?.map((unit) => unit.unitcode) || [];
            const params = {
                unitCodes: unitcodes
            };

            if (searchText) {
                params.searchText = searchText;
            }

            const getUnitsResult = await getUnits(params);
            const units = getUnitsResult?.data || [];

            let docDefinition = await generateDocumentDefinition(course, units);
            generatePdf(docDefinition, `Course Summary - ${course.title} - ${formatDate(new Date())}.pdf`);
        } catch (error) {
            console.error(error);
        }
    };
}

export const fetchUnits = (params) => {
    return async (dispatch) => {
        try {
            dispatch(courses.actions.setLoadingUnits(true));
            const result = await getUnits(params);
            dispatch(courses.actions.setUnits(result?.data || []));
        } catch (error) {
            dispatch(courses.actions.setUnits([]));
        } finally {
            dispatch(courses.actions.setLoadingUnits(false));
        }
    };
};

export const createCourse = (course) => {
    return async (dispatch) => {
        try {
            // Assuming there's an API call to create a course
            const response = await createCourseApi(course);
            if (response.status) {
                await dispatch(fetchCourses());
                return { success: true, message: 'Course created successfully.' };
            } else {
                return { success: false, message: response.message || 'Failed to create course.' };
            }
        } catch (error) {
            return { success: false, message: error.message || 'Failed to create course.' };
        }
    };
};

export const editCourse = (id, course) => {
    return async (dispatch) => {
        try {
            const response = await editCourseApi(id, course);
            if (response.status) {
                await dispatch(fetchCourses());
                return { success: true, message: 'Course updated successfully.' };
            } else {
                return { success: false, message: response.message || 'Failed to update course.' };
            }
        } catch (error) {
            console.error(error);
            return { success: false, message: error.message || 'Failed to edit course.' };
        }
    };
};

export const copyCourse = (id) => {
    return async (dispatch) => {
        try {
            // Assuming there's an API call to copy a course
            const response = await copyCourseApi(id);
            if (response.status) {
                await dispatch(fetchCourses());
                return { success: true, message: 'Course copied successfully.' };
            } else {
                return { success: false, message: response.message || 'Failed to copy course.' };
            }
        } catch (error) {
            return { success: false, message: error.message || 'Failed to copy course.' };
        }
    };
};
